'use client'

import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState, type TouchEvent } from 'react'
import { landingpage_freeuse } from '@/constants/trace'
import { handleUETClick } from '@/lib/utils'
import { Container } from './container'
import { Heading } from './heading'
import { ImmediateUseButton } from './immediate-use-button'

const scenarios = [
  {
    key: 'report',
    label: '职场汇报',
    title: '助力每一次关键汇报',
    img: 'https://cdn.aitubiao.com/static/images/itubiao/landingpage/scenarios-rb.jpg',
    description: 'AI 自动提炼核心数据结论，一键匹配企业品牌主色。支持 PPT 原生导出，助你从容应对每一场关键会议。'
  },
  {
    key: 'homework',
    label: '课程作业',
    title: '不止完成作业',
    img: 'https://cdn.aitubiao.com/static/images/itubiao/landingpage/scenarios-kczy.jpg',
    description: `自动整理数据与内容结构，生成清晰的图表与作业表达。
从资料到结论一键完成，支持多轮修改与优化，
让你的作业更有逻辑、更有说服力。`
  },
  {
    key: 'finance',
    label: '财务分析',
    title: '让分析更直观',
    img: 'https://cdn.aitubiao.com/static/images/itubiao/landingpage/scenarios-cb.jpg',
    description: `快速生成多维度财务图表，清晰呈现数据变化与关键趋势，
帮助你更高效理解与表达数据。`
  },
  {
    key: 'media',
    label: '媒体传播',
    title: '好内容，不该被数据劝退',
    img: 'https://cdn.aitubiao.com/static/images/itubiao/landingpage/scenarios-smcb.jpg',
    description: `把复杂数据自动变成可传播的图文内容。
从信息拆解到视觉表达一键完成，适配多平台分发，
让数据内容更容易被理解、被转发。`
  },
  {
    key: 'sales',
    label: '销售复盘',
    title: '看结构，更看原因',
    img: 'https://cdn.aitubiao.com/static/images/itubiao/landingpage/scenarios-xsfp.jpg',
    description: `自动汇总销售数据，识别趋势变化与关键问题。
从结果拆解到原因分析一键完成，支持多轮复盘与对比，
让每一次复盘都有结论、有行动方向。`
  },
  {
    key: 'research',
    label: '研究报告',
    title: '研究不是难，难的是表达清楚',
    img: 'https://cdn.aitubiao.com/static/images/itubiao/landingpage/scenarios-xsdp.jpg',
    description: `适用于学术研究与咨询分析，自动生成图表与报告结构，
帮助你高效呈现复杂研究内容，提升表达质量。`
  }
] as const

const MOBILE_MEDIA_QUERY = '(max-width: 767px)'
const AUTO_PLAY_INTERVAL = 4000
const SWIPE_THRESHOLD = 48

type SwipePoint = {
  x: number
  y: number
}

export function Scenarios() {
  const searchParams = useSearchParams()
  const [activeIndex, setActiveIndex] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const touchStartRef = useRef<SwipePoint | null>(null)
  const touchDeltaRef = useRef<SwipePoint>({ x: 0, y: 0 })

  const queryString = searchParams.toString()
  const loginPath = queryString ? `https://app.aitubiao.com/sign-in?${queryString}` : 'https://app.aitubiao.com/sign-in'

  const activeScenario = useMemo(() => scenarios[activeIndex] ?? scenarios[0], [activeIndex])

  useEffect(() => {
    // 只在客户端判断移动端断点，避免把自动轮播错误地带到桌面端。
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY)
    const syncMobileState = () => {
      setIsMobile(mediaQuery.matches)
    }

    syncMobileState()
    mediaQuery.addEventListener('change', syncMobileState)

    return () => {
      mediaQuery.removeEventListener('change', syncMobileState)
    }
  }, [])

  useEffect(() => {
    if (!isMobile) {
      return
    }

    // 移动端按用户要求自动轮播；这里用 timeout，这样手动切换后会自动重置播放节奏。
    const autoplayTimer = window.setTimeout(() => {
      setActiveIndex(current => (current + 1) % scenarios.length)
    }, AUTO_PLAY_INTERVAL)

    return () => {
      window.clearTimeout(autoplayTimer)
    }
  }, [activeIndex, isMobile])

  const resetSwipe = () => {
    touchStartRef.current = null
    touchDeltaRef.current = { x: 0, y: 0 }
  }

  const handlePrev = () => {
    setActiveIndex(current => (current - 1 + scenarios.length) % scenarios.length)
  }

  const handleNext = () => {
    setActiveIndex(current => (current + 1) % scenarios.length)
  }

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (!isMobile) {
      return
    }

    const touch = event.touches[0]
    touchStartRef.current = { x: touch.clientX, y: touch.clientY }
    touchDeltaRef.current = { x: 0, y: 0 }
  }

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (!isMobile || !touchStartRef.current) {
      return
    }

    const touch = event.touches[0]
    touchDeltaRef.current = {
      x: touch.clientX - touchStartRef.current.x,
      y: touch.clientY - touchStartRef.current.y
    }
  }

  const handleTouchEnd = () => {
    if (!isMobile || !touchStartRef.current) {
      resetSwipe()
      return
    }

    const deltaX = touchDeltaRef.current.x
    const deltaY = touchDeltaRef.current.y

    // 只有横向位移明显大于纵向滚动时才翻页，避免用户竖向滑动页面时误触场景切换。
    if (Math.abs(deltaX) > SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX < 0) {
        handleNext()
      } else {
        handlePrev()
      }
    }

    resetSwipe()
  }

  return (
    <section className="py-24 md:py-28">
      <Container>
        <Heading as="h2" className="text-[34px] font-semibold leading-tight text-[#353745] md:text-5xl lg:text-6xl">
          <>
            轻松点亮每一个 <span className="text-primary">「高光时刻」</span>
          </>
        </Heading>

        {/* 这里改回 Figma 的轻量 tab 排布，不再套额外大渐变外壳。 */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3 md:mt-12 md:gap-4">
          {scenarios.map((item, index) => {
            const isActive = index === activeIndex

            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={isActive ? activeTabClassName : inactiveTabClassName}
                aria-pressed={isActive}
              >
                {item.label}
              </button>
            )
          })}
        </div>

        <div
          className="mx-auto mt-14 flex max-w-[1200px] touch-pan-y flex-col items-center gap-8 lg:flex-row lg:items-center lg:gap-10"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={resetSwipe}
        >
          <div className="w-full max-w-[760px] overflow-hidden">
            <Image src={activeScenario.img} alt={`${activeScenario.label}场景预览`} width={760} height={418} className="h-auto w-full" priority />
          </div>

          <div className="flex w-full flex-col gap-10 px-6 lg:max-w-[400px] lg:px-0 lg:py-12">
            <div className="space-y-6 text-center lg:text-left">
              <h3 className="text-[32px] font-semibold leading-[1.18] text-[#353745] md:text-[38px] lg:text-[40px]">{activeScenario.title}</h3>
              <p className="text-base leading-8 text-[#555974] md:text-lg">{activeScenario.description}</p>
            </div>

            <div className="hidden items-center justify-center gap-[100px] px-10 sm:flex">
              <button type="button" onClick={handlePrev} className={arrowButtonClassName} aria-label="查看上一个场景">
                <ArrowLeftIcon />
              </button>
              <button type="button" onClick={handleNext} className={arrowButtonClassName} aria-label="查看下一个场景">
                <ArrowRightIcon />
              </button>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-16 flex justify-center text-center" onClickCapture={handleUETClick}>
          <ImmediateUseButton href={loginPath} traceEvent={{ event: landingpage_freeuse }} icon={<ActionIcon className="h-10 w-10" />} />
        </div>
      </Container>
    </section>
  )
}

const activeTabClassName =
  'inline-flex h-[46px] min-w-[118px] items-center justify-center rounded-xl bg-[#685EF6] px-5 text-sm font-semibold text-white transition duration-200 md:px-6 md:text-base'

const inactiveTabClassName =
  'inline-flex h-[46px] min-w-[118px] items-center justify-center rounded-xl border border-[#C5CCFF] bg-white px-5 text-sm font-medium text-[#555974] transition duration-200 hover:border-[#685EF6] hover:text-[#493FD1] md:px-6 md:text-base'

const arrowButtonClassName =
  'flex h-16 w-16 items-center justify-center rounded-full border border-[#D8DEF5] bg-white text-[#7B8099] transition duration-200 hover:border-[#685EF6] hover:text-[#685EF6]'

const ArrowLeftIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <path d="M17 11H5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M10.5 5.5L5 11L10.5 16.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const ArrowRightIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      <path d="M5 11H17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M11.5 5.5L17 11L11.5 16.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const ActionIcon = ({ className }: { className?: string }) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true" className={className}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20.0551 9.64268C20.5046 8.02337 22.7874 7.98684 23.2885 9.59092L23.5912 10.5597C24.6889 14.0745 27.2599 16.9385 30.6361 18.4083C31.9152 18.9653 31.892 20.7888 30.6058 21.3292C27.015 22.8377 24.2877 25.9072 23.2289 29.6554L22.4818 32.3019C22.2148 33.2473 20.8819 33.2684 20.5844 32.3321L19.6537 29.4024C18.5087 25.7997 15.8245 22.8734 12.3392 21.4093C11.0181 20.8543 11.0254 18.9719 12.3568 18.4425C16.0781 16.9633 18.8931 13.8275 19.9642 9.96885L20.0551 9.64268ZM30.3373 26.6124C30.4565 26.2062 31.0325 26.2063 31.1517 26.6124L31.2621 26.9903C31.5265 27.8935 32.2434 28.5933 33.1527 28.836C33.4501 28.9155 33.4936 29.3198 33.2201 29.461L32.9672 29.5909C32.1464 30.0146 31.5217 30.74 31.224 31.6144L31.0394 32.1554C30.9447 32.4337 30.5511 32.4317 30.4594 32.1524L30.2445 31.5001C29.9614 30.6378 29.3359 29.9293 28.515 29.5421L28.3099 29.4454C28.0322 29.3141 28.0687 28.9076 28.3656 28.8282C29.2567 28.5903 29.9589 27.9047 30.2181 27.0196L30.3373 26.6124ZM9.88709 5.1085C10.0189 4.63989 10.6829 4.63999 10.8148 5.1085L11.1947 6.45811C11.723 8.33715 13.2335 9.77883 15.1351 10.2189C15.4569 10.2933 15.4869 10.7395 15.1781 10.8565L14.9564 10.9405C12.8256 11.7478 11.2732 13.6127 10.8656 15.8546L10.7416 16.5353C10.6489 17.0433 9.93058 17.0703 9.80018 16.5704L9.46424 15.2843C8.95223 13.3214 7.54604 11.714 5.66834 10.9464L5.47987 10.8692C5.17478 10.7445 5.21051 10.3019 5.53163 10.2276C7.45531 9.78247 8.98349 8.32377 9.51795 6.42295L9.88709 5.1085Z"
        fill="white"
      />
    </svg>
  )
}
